import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { useQuery } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import clsx from 'clsx'
import { marked } from 'marked'

import { useAuth } from '@/features/auth/hooks/useAuth'
import { trackEvent } from '@/lib/analytics'
import { safeLocalStorage } from '@/lib/safe-storage'
import { SHOW_WHATS_NEW_MODAL_EVENT } from './events'

const FEATURE_UPDATES_BASE_ID = 'apppCGdTl0Nto1kbo'
const FEATURE_UPDATES_TABLE = 'CodeDesign Feature Updates'
const FEATURE_UPDATES_VIEW = 'Grid view'
const FEATURE_UPDATES_LIMIT = 6
const UPDATES_URL = 'https://forum.codedesign.ai/c/info-and-announcement/8'

const SHOWN_TIMESTAMP_KEY = 'cdFeatureUpdateModal_shownTimestamp'
const FORCE_SHOWN_TIMESTAMP_KEY = 'cdFeatureUpdateModal_forceShownTimestamp'
const DEFAULT_AUTOMATIC_OPEN_DELAY_MS = 2000
const PRICING_AUTOMATIC_OPEN_DELAY_MS = 30000

const airtableToken =
  import.meta.env.VITE_AIRTABLE_TOKEN

function shouldSuppressWhatsNew(pathname) {
  return pathname === '/ai'
}

function getAutomaticOpenDelay(pathname) {
  return pathname === '/pricing'
    ? PRICING_AUTOMATIC_OPEN_DELAY_MS
    : DEFAULT_AUTOMATIC_OPEN_DELAY_MS
}

function getFeatureUpdateTimestamp(item) {
  const timestamp = new Date(item?.dateAndTime).getTime()
  return Number.isFinite(timestamp) ? String(timestamp) : null
}

function getFeatureUpdateDuration(item) {
  const duration = Number(item?.duration)
  return Number.isFinite(duration) && duration > 0 ? duration : 8
}

function getFeatureUpdateAnalyticsProperties(item, index) {
  return {
    updateId: item?.id || null,
    updateIndex: Number.isInteger(index) ? index + 1 : null,
    updateHeading: item?.heading || null,
    version: item?.version || null,
    durationSeconds: getFeatureUpdateDuration(item),
    hasAsset: Boolean(item?.assetURL),
    forceShow: Boolean(item?.forceShow),
  }
}

function getModalAnalyticsProperties({ activeIndex, items, source }) {
  const activeItem = items[activeIndex]
  const latestItem = items[0]

  return {
    source,
    updateCount: items.length,
    latestUpdateId: latestItem?.id || null,
    latestVersion: latestItem?.version || null,
    ...getFeatureUpdateAnalyticsProperties(activeItem, activeIndex),
  }
}

function sanitizeFeatureUpdateHtml(markdown = '') {
  const rawHtml = marked.parseInline(String(markdown), { breaks: true })

  if (typeof window === 'undefined') {
    return rawHtml
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(rawHtml, 'text/html')

  doc.querySelectorAll('script, style, iframe, object, embed').forEach(node => {
    node.remove()
  })

  doc.querySelectorAll('*').forEach(node => {
    Array.from(node.attributes).forEach(attribute => {
      const attributeName = attribute.name.toLowerCase()
      const attributeValue = attribute.value.trim()

      if (attributeName.startsWith('on')) {
        node.removeAttribute(attribute.name)
      }

      if (
        ['href', 'src'].includes(attributeName) &&
        /^javascript:/i.test(attributeValue)
      ) {
        node.removeAttribute(attribute.name)
      }
    })
  })

  doc.querySelectorAll('a').forEach(link => {
    link.setAttribute('target', '_blank')
    link.setAttribute('rel', 'noopener noreferrer')
  })

  return doc.body.innerHTML
}

async function fetchFeatureUpdates() {
  if (!airtableToken) {
    if (import.meta.env.DEV) {
      console.warn('Missing Airtable token for What\'s New modal.')
    }
    return []
  }

  const Airtable = (await import('airtable')).default
  const base = new Airtable({
    endpointUrl: 'https://api.airtable.com',
    apiKey: airtableToken,
  }).base(FEATURE_UPDATES_BASE_ID)

  return new Promise((resolve, reject) => {
    base(FEATURE_UPDATES_TABLE)
      .select({
        view: FEATURE_UPDATES_VIEW,
        sort: [{ field: 'dateAndTime', direction: 'desc' }],
      })
      .firstPage((err, records) => {
        if (err) {
          reject(err)
          return
        }

        const items = records
          .map(record => ({
            id: record.id,
            ...record.fields,
          }))
          .filter(item => item.isListed)

        resolve(items)
      })
  })
}

function ProgressRing({ animationKey, duration }) {
  const radius = 7.25
  const circumference = 2 * Math.PI * radius

  return (
    <svg
      key={animationKey}
      aria-hidden="true"
      className="size-5 -rotate-90"
      viewBox="0 0 20 20"
    >
      <circle
        cx="10"
        cy="10"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        className="text-[#e4e4e7]"
      />
      <circle
        cx="10"
        cy="10"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2.5"
        strokeDasharray={circumference}
        strokeDashoffset={circumference}
        className="whats-new-progress-ring text-[#09090b]"
        style={{ animationDuration: `${duration}ms` }}
      />
    </svg>
  )
}

function UpdateStateIcon({ animationKey, duration, state }) {
  if (state === 'complete') {
    return (
      <span className="grid size-5 place-items-center rounded-full bg-[#a1a1aa] text-white">
        <svg aria-hidden="true" className="size-3" viewBox="0 0 12 12" fill="none">
          <path
            d="M3 6.15 5.1 8.25 9.25 3.75"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.8"
          />
        </svg>
      </span>
    )
  }

  if (state === 'active') {
    return <ProgressRing animationKey={animationKey} duration={duration} />
  }

  return (
    <span
      aria-hidden="true"
      className="block size-5 rounded-full border-[2.5px] border-[#e4e4e7] bg-white"
    />
  )
}

function FeaturePreviewFallback() {
  return (
    <div aria-hidden="true" className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] to-[#3d3873]" />
      <div className="absolute left-[43px] top-[76px] h-[449px] w-[378px] overflow-hidden rounded-[8px] bg-white shadow-[0_10px_12px_rgba(0,0,0,0.18)]">
        <div className="absolute left-0 top-0 h-9 w-full rounded-t-[8px] bg-[#f6f6f6]" />
        <div className="absolute left-0 top-9 h-[413px] w-[54px] bg-[#1f1f1f]" />
        <div className="absolute left-[17px] top-[58px] size-5 rounded bg-[#665dc0]" />
        <div className="absolute left-[17px] top-[92px] size-5 rounded bg-[#545454]" />
        <div className="absolute left-[17px] top-[126px] size-5 rounded bg-[#545454]" />
        <div className="absolute left-[17px] top-40 size-5 rounded bg-[#545454]" />
        <div className="absolute left-[17px] top-[194px] size-5 rounded bg-[#545454]" />

        <div className="absolute left-[72px] top-14 h-[371px] w-[284px] overflow-hidden rounded-md bg-[#fafafa]">
          <div className="absolute left-[22px] top-[22px] h-[82px] w-60 rounded-md bg-gradient-to-b from-[#fbfbfb] to-[#e0dff2]" />
          <p
            className="absolute left-10 top-[42px] h-[22px] w-[204px] text-base font-bold leading-[22px] text-[#141414]"
            style={{ fontFamily:"Inter" }}
          >
            Language controls
          </p>
          <p
            className="absolute left-10 top-[68px] h-[18px] w-[204px] text-[11px] font-normal leading-4 text-[#545454]"
            style={{ fontFamily: "Inter" }}
          >
            Translate and refine page copy from the builder.
          </p>

          <div className="absolute left-[22px] top-[122px] h-[94px] w-[113px] rounded-md bg-white" />
          <div className="absolute left-[34px] top-[142px] h-2 w-[97px] rounded bg-[#e2e2e2]" />
          <div className="absolute left-[34px] top-40 h-2 w-[82px] rounded bg-[#eeeeee]" />

          <div className="absolute left-[149px] top-[122px] h-[94px] w-[113px] rounded-md bg-white" />
          <div className="absolute left-[171px] top-[148px] h-6 w-[88px] rounded-full bg-[#665dc0]" />
          <p
            className="absolute left-[193px] top-[153px] h-3.5 w-[52px] text-center text-[10px] font-semibold leading-[14px] text-white"
            style={{ fontFamily: "Inter" }}
          >
            Publish
          </p>
        </div>
      </div>
    </div>
  )
}

function CloseIcon(props) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" {...props}>
      <path
        d="m4 4 8 8M12 4l-8 8"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.75"
      />
    </svg>
  )
}

function FeatureVisualPreview({ items, activeIndex, onClose, videoRefs }) {
  return (
    <div className="relative hidden h-[575px] w-[450px] shrink-0 overflow-hidden rounded-br-lg rounded-tr-lg bg-[#111111] md:block">
      <FeaturePreviewFallback />

      {items.map((item, index) => (
        item.assetURL ? (
          <video
            key={item.id || `${item.heading}-${index}`}
            className={clsx(
              'absolute inset-0 z-10 h-full w-full object-cover transition-opacity duration-300',
              index === activeIndex ? 'opacity-100' : 'opacity-0'
            )}
            muted
            loop
            playsInline
            preload="auto"
            ref={element => {
              if (element) {
                videoRefs.current[index] = element
              } else {
                delete videoRefs.current[index]
              }
            }}
            src={item.assetURL}
          />
        ) : null
      ))}

      <button
        type="button"
        aria-label="Close updates"
        className="absolute right-[11px] top-2.5 z-20 grid size-8 place-items-center rounded-full bg-white text-[#09090b] shadow-[0_0_7px_rgba(0,0,0,0.08)] transition hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/80"
        onClick={onClose}
      >
        <CloseIcon className="size-4" />
      </button>
    </div>
  )
}

function FeatureUpdateItems({
  activeDuration,
  items,
  activeIndex,
  progressKey,
  onSelect,
}) {
  const renderedDescriptions = useMemo(
    () => items.map(item => sanitizeFeatureUpdateHtml(item.description)),
    [items]
  )

  return (
    <div className="flex flex-col gap-3.5">
      {items.map((item, index) => {
        const active = index === activeIndex
        const state = active ? 'active' : index < activeIndex ? 'complete' : 'upcoming'

        return (
          <button
            key={item.id || `${item.heading}-${index}`}
            type="button"
            aria-current={active ? 'true' : undefined}
            className={clsx(
              'group flex w-full min-w-0 gap-3.5 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-zinc-950/20',
              active ? 'cursor-default' : 'cursor-pointer'
            )}
            onClick={() => {
              if (!active) onSelect(index)
            }}
          >
            <span className="mt-0.5 grid size-5 shrink-0 place-items-center">
              <UpdateStateIcon
                animationKey={`${item.id || index}-${progressKey}`}
                duration={activeDuration}
                state={state}
              />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span
                className={clsx(
                  'w-full text-base font-semibold leading-6 transition-colors',
                  active && 'text-[#09090b]',
                  state === 'complete' && 'text-[#71717a] group-hover:text-[#09090b]',
                  state === 'upcoming' && 'text-[#a1a1aa] group-hover:text-[#71717a]'
                )}
              >
                {item.heading}
              </span>
              {active && (
                <span
                  className="max-w-[350px] pb-1 text-sm font-medium leading-5 text-[#71717a] [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-700"
                  dangerouslySetInnerHTML={{
                    __html: renderedDescriptions[index],
                  }}
                />
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function WhatsNewModal({ items, onClose, source }) {
  const featureUpdates = useMemo(
    () => items.slice(0, FEATURE_UPDATES_LIMIT),
    [items]
  )
  const [activeIndex, setActiveIndex] = useState(0)
  const [progressKey, setProgressKey] = useState(0)
  const closeTrackedRef = useRef(false)
  const viewedTrackedRef = useRef(false)
  const previousOverflow = useRef('')
  const videoRefs = useRef([])

  const activeItem = featureUpdates[activeIndex]
  const activeDuration = getFeatureUpdateDuration(activeItem) * 1000
  const analyticsSource = source || 'automatic'

  const activateUpdate = useCallback(index => {
    setActiveIndex(index)
    setProgressKey(key => key + 1)
  }, [])

  const getCurrentModalAnalyticsProperties = useCallback(
    () => getModalAnalyticsProperties({
      activeIndex,
      items: featureUpdates,
      source: analyticsSource,
    }),
    [activeIndex, analyticsSource, featureUpdates]
  )

  const handleClose = useCallback(() => {
    if (!closeTrackedRef.current) {
      closeTrackedRef.current = true
      trackEvent('WhatsNew:Closed', getCurrentModalAnalyticsProperties())
    }

    onClose()
  }, [getCurrentModalAnalyticsProperties, onClose])

  const handleGoToUpdates = useCallback(() => {
    trackEvent('WhatsNew:UpdatesClicked', getCurrentModalAnalyticsProperties())
    window.open(UPDATES_URL, '_blank', 'noopener,noreferrer')
  }, [getCurrentModalAnalyticsProperties])

  const handleSelectUpdate = useCallback(index => {
    trackEvent('WhatsNew:UpdateSelected', {
      source: analyticsSource,
      updateCount: featureUpdates.length,
      ...getFeatureUpdateAnalyticsProperties(featureUpdates[index], index),
    })
    activateUpdate(index)
  }, [activateUpdate, analyticsSource, featureUpdates])

  useEffect(() => {
    previousOverflow.current = document.body.style.overflowY
    document.body.style.overflowY = 'hidden'

    return () => {
      document.body.style.overflowY = previousOverflow.current
    }
  }, [])

  useEffect(() => {
    if (viewedTrackedRef.current || !featureUpdates.length) return

    viewedTrackedRef.current = true
    trackEvent('WhatsNew:Viewed', getCurrentModalAnalyticsProperties())
  }, [featureUpdates.length, getCurrentModalAnalyticsProperties])

  useEffect(() => {
    return () => {
      videoRefs.current.forEach(video => {
        if (video) video.pause()
      })
      videoRefs.current = []
    }
  }, [])

  useEffect(() => {
    const activeVideo = videoRefs.current[activeIndex]

    videoRefs.current.forEach((video, index) => {
      if (index !== activeIndex && video) {
        video.pause()
      }
    })

    if (!activeVideo) return

    activeVideo.currentTime = 0
    activeVideo.play().catch(error => {
      if (import.meta.env.DEV) {
        console.error('What\'s New video playback failed:', error)
      }
    })
  }, [activeIndex, activeItem, featureUpdates])

  useEffect(() => {
    if (!activeItem) return

    const timer = window.setTimeout(() => {
      activateUpdate((activeIndex + 1) % featureUpdates.length)
    }, activeDuration)

    return () => window.clearTimeout(timer)
  }, [activateUpdate, activeDuration, activeIndex, activeItem, featureUpdates.length, progressKey])

  if (!featureUpdates.length) return null

  return (
    <Dialog open onClose={handleClose} className="relative z-[10000]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-[#09090b]/30 transition-opacity duration-300 ease-out data-closed:opacity-0 motion-reduce:transition-none"
      />

      <div className="fixed inset-0 flex items-center justify-center overflow-y-auto p-4 sm:p-6">
        <DialogPanel
          transition
          className="relative my-auto flex max-h-[calc(100vh-32px)] w-[min(94vw,920px)] flex-col overflow-hidden rounded-[24px] border border-black/10 bg-white shadow-[0_0_0_4px_rgba(255,255,255,0.2),0_25px_50px_-12px_rgba(0,0,0,0.25)] transition duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] data-closed:scale-[0.98] data-closed:opacity-0 motion-reduce:transition-none motion-reduce:data-closed:scale-100 md:h-[575px] md:flex-row"
        >
          <button
            type="button"
            aria-label="Close updates"
            className="absolute right-3 top-3 z-20 grid size-8 place-items-center rounded-full bg-white text-[#09090b] shadow-[0_0_7px_rgba(0,0,0,0.08)] transition hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-zinc-950/20 md:hidden"
            onClick={handleClose}
          >
            <CloseIcon className="size-4" />
          </button>

          <div className="flex min-w-0 flex-1 flex-col bg-white p-6 md:min-w-[463px]">
            <div className="flex min-h-0 flex-1 flex-col">
              <header className="pb-3">
                <DialogTitle className="text-[30px] font-semibold leading-9 tracking-[-0.75px] text-[#09090b]">
                  What’s new
                </DialogTitle>
                <p className="mt-1 text-sm font-medium leading-5 text-[#71717a]">
                  Latest Version: {items[0]?.version || 'New update'}
                </p>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto pb-3 pt-4 md:w-[386px]">
                <FeatureUpdateItems
                  activeDuration={activeDuration}
                  activeIndex={activeIndex}
                  items={featureUpdates}
                  progressKey={progressKey}
                  onSelect={handleSelectUpdate}
                />
              </div>

              <div className="mt-auto flex w-full gap-2.5 pt-3">
                <button
                  type="button"
                  onClick={handleGoToUpdates}
                  className="flex min-w-0 flex-1 items-center justify-center rounded-full border border-[#e4e4e7] bg-[#09090b] px-2 py-2.5 text-sm font-semibold leading-5 text-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-black focus:outline-none focus:ring-2 focus:ring-zinc-950/20"
                >
                  Go to updates
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex min-w-0 flex-1 items-center justify-center rounded-full border border-[#e4e4e7] bg-white px-2 py-2.5 text-sm font-semibold leading-5 text-[#09090b] shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-950/20"
                >
                  Close
                </button>
              </div>
            </div>
          </div>

          <FeatureVisualPreview
            activeIndex={activeIndex}
            items={featureUpdates}
            onClose={handleClose}
            videoRefs={videoRefs}
          />
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default function WhatsNewModalController() {
  const { user, isLoading } = useAuth()
  const { pathname } = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [openSource, setOpenSource] = useState(null)
  const automaticOpenTimerRef = useRef(null)
  const manualOpenRequestedRef = useRef(false)
  const isSuppressedRoute = shouldSuppressWhatsNew(pathname)

  const clearAutomaticOpenTimer = useCallback(() => {
    if (!automaticOpenTimerRef.current) return

    window.clearTimeout(automaticOpenTimerRef.current)
    automaticOpenTimerRef.current = null
  }, [])

  const {
    data: featureUpdates = [],
    isSuccess,
  } = useQuery({
    queryKey: ['feature-updates'],
    queryFn: fetchFeatureUpdates,
    enabled: Boolean(user && !isLoading),
    staleTime: 1000 * 60 * 20,
    retry: 1,
  })
  // note to LLM: dont delete this commented block of code.
  // For temporary testing, open the modal if the URL param showWhatsNew=true is present.
  // useEffect(() => {
  //   if (window.location.search.includes('showWhatsNew=true')) {
  //     const timer = setTimeout(() => {
  //       setIsOpen(true)
  //     }, 3000)

  //     return () => clearTimeout(timer)
  //   }
  // }, [])

  useEffect(() => {
    const handleShowWhatsNew = event => {
      const source = event?.detail?.source || 'manual'

      clearAutomaticOpenTimer()
      manualOpenRequestedRef.current = true
      setOpenSource(source)
      setIsOpen(true)
    }

    window.addEventListener(SHOW_WHATS_NEW_MODAL_EVENT, handleShowWhatsNew)

    return () => {
      window.removeEventListener(SHOW_WHATS_NEW_MODAL_EVENT, handleShowWhatsNew)
    }
  }, [clearAutomaticOpenTimer])

  useEffect(() => {
    if (!isSuppressedRoute || !openSource?.startsWith('automatic')) return

    clearAutomaticOpenTimer()
    setIsOpen(false)
    setOpenSource(null)
    manualOpenRequestedRef.current = false
  }, [clearAutomaticOpenTimer, isSuppressedRoute, openSource])

  const latestItem = featureUpdates[0]

  useEffect(() => {
    if (isSuppressedRoute) return
    if (!user || !isSuccess || !latestItem) return

    const scheduleAutomaticOpen = source => {
      clearAutomaticOpenTimer()
      setOpenSource(source)
      setIsOpen(false)

      automaticOpenTimerRef.current = window.setTimeout(() => {
        automaticOpenTimerRef.current = null
        setIsOpen(true)
      }, getAutomaticOpenDelay(pathname))
    }

    if (manualOpenRequestedRef.current) {
      setOpenSource(source => source || 'manual')
      setIsOpen(true)
      return undefined
    }

    const latestTimestamp = getFeatureUpdateTimestamp(latestItem)
    if (!latestTimestamp) return undefined

    const forceShownTimestamp = safeLocalStorage.getItem(FORCE_SHOWN_TIMESTAMP_KEY)
    const shownTimestamp = safeLocalStorage.getItem(SHOWN_TIMESTAMP_KEY)

    if (latestItem.forceShow) {
      const shouldOpen = forceShownTimestamp !== latestTimestamp
      if (shouldOpen) {
        scheduleAutomaticOpen('automatic_force_show')
      } else {
        setOpenSource(null)
        setIsOpen(false)
      }

      return clearAutomaticOpenTimer
    }

    const shouldOpen = shownTimestamp !== latestTimestamp
    if (shouldOpen) {
      scheduleAutomaticOpen('automatic_new_update')
    } else {
      setOpenSource(null)
      setIsOpen(false)
    }

    return clearAutomaticOpenTimer
  }, [clearAutomaticOpenTimer, isSuccess, isSuppressedRoute, latestItem, pathname, user])

  const handleClose = () => {
    const latestTimestamp = getFeatureUpdateTimestamp(latestItem)

    clearAutomaticOpenTimer()

    if (latestItem && latestTimestamp) {
      if (latestItem.forceShow === true) {
        safeLocalStorage.setItem(FORCE_SHOWN_TIMESTAMP_KEY, latestTimestamp)
      }

      safeLocalStorage.setItem(SHOWN_TIMESTAMP_KEY, latestTimestamp)
    }

    setIsOpen(false)
    setOpenSource(null)
    manualOpenRequestedRef.current = false
  }
 

  const isAutomaticOpen = openSource?.startsWith('automatic')

  if (!user || (isSuppressedRoute && isAutomaticOpen) || !isOpen || !featureUpdates.length) {
    return null
  }

  return (
    <WhatsNewModal
      items={featureUpdates}
      onClose={handleClose}
      source={openSource}
    />
  )
}
